"use client";

import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { getAlternateUrl } from "@/lib/i18n/slug-map";
import type { Locale } from "./templates/ServicePageTemplate";

const languages: { code: Locale; flag: string }[] = [
  { code: "sv", flag: "🇸🇪" },
  { code: "en", flag: "🇬🇧" },
];

function useHrefs(locale: Locale) {
  const pathname = usePathname();
  return Object.fromEntries(
    languages.map((l) => [
      l.code,
      l.code === locale ? pathname : getAlternateUrl(pathname, l.code),
    ])
  ) as Record<Locale, string>;
}

export default function LanguageSelector({ locale = "sv" }: { locale?: Locale }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hrefs = useHrefs(locale);
  const selected = languages.find((l) => l.code === locale) ?? languages[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex items-center justify-center rounded-xl px-3 py-2 lg:py-2.5 text-lg leading-none",
          "hover:bg-gray-50 transition-all"
        )}
      >
        <span className="grid place-items-center w-[1em] h-[1em] translate-y-[2px]">{selected.flag}</span>
      </button>

      {open && (
        <div
          className={cn(
            "absolute right-0 mt-2 rounded-xl overflow-hidden",
            "bg-white/95 backdrop-blur-xl",
            "shadow-lg border border-gray-200",
            "animate-[fade-in_0.15s_ease-out]"
          )}
        >
          {languages.map((lang) => (
            <a
              key={lang.code}
              href={hrefs[lang.code]}
              className={cn(
                "flex items-center justify-center px-3 py-2 text-lg leading-none transition-colors",
                selected.code === lang.code
                  ? "bg-brand-secondary/10"
                  : "hover:bg-gray-50"
              )}
            >
              {lang.flag}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline version for mobile nav — just two flag buttons */
export function LanguageSelectorInline({ locale = "sv" }: { locale?: Locale }) {
  const hrefs = useHrefs(locale);
  return (
    <div className="flex gap-2">
      {languages.map((lang) => (
        <a
          key={lang.code}
          href={hrefs[lang.code]}
          className={cn(
            "flex items-center justify-center flex-1 rounded-xl px-4 py-3 text-xl leading-none transition-colors",
            locale === lang.code
              ? "bg-brand-secondary/10 border border-brand-secondary/20"
              : "bg-gray-50 hover:bg-gray-100"
          )}
        >
          {lang.flag}
        </a>
      ))}
    </div>
  );
}
